import { ReactNode, useRef, useState } from "react";
import { SWRResponse } from "swr";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { CustomError } from "../../http/errors";
import { Paginated, PaginationQuery, Version, Versions } from "../../types";
import { formatTimeStamp } from "../../types/utils";
import styles from "./HistoryDialog.module.css";
import ModalDialog from "./ModalDialog";
import Table from "./Table";
import VersionDialog from "./VersionDialog";

type HistoryDialogProps<T extends Version> = {
  title: string;
  entityId: string;
  lastVersion: number;
  onClose: () => void;
  getHistory: (
    query: PaginationQuery,
    entityId: string,
    signal: AbortSignal
  ) => SWRResponse<Paginated<Version> | null, CustomError>;
  getEntityVersionHook: (
    entityId: string,
    versionNumber: number,
    signal: AbortSignal
  ) => SWRResponse<Versions<T> | null, CustomError, any>;
  renderForm: (headers: string[], data: Versions<T>) => ReactNode;
};

const HistoryDialog = <T extends Version>({ title, entityId, onClose, getHistory, ...rest }: HistoryDialogProps<T>) => {
  const i18n = useI18n();
  const filters = useListFilters();
  const debouncedQ = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [versionToShow, setVersionToShow] = useState<Version | undefined>();

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedQ,
  };

  const { data, error, isLoading, isValidating } = getHistory(query, entityId, aborter.current.signal);

  return (
    <>
      <ModalDialog onClose={onClose} title={title} className={styles.dialog}>
        <Table<Version>
          filters={filters}
          head={
            <>
              <th align="left">{i18n.t.common.versionNumber}</th>
              <th align="left">{i18n.t.common.createdBy}</th>
              <th align="left">{i18n.t.common.when}</th>
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            setVersionToShow(item);
          }}
          renderRow={(item) => (
            <>
              <td>{item.versionNumber}</td>
              <td>{item.author?.fullName}</td>
              <td>{formatTimeStamp(item.createdAt, i18n.language)}</td>
            </>
          )}
          items={data?.items}
          total={data?.total}
          error={isLoading || isValidating ? error : undefined}
        />
      </ModalDialog>

      {versionToShow && (
        <ModalDialog onClose={() => setVersionToShow(undefined)}>
          <VersionDialog<T> entityId={entityId} version={versionToShow} {...rest} />
        </ModalDialog>
      )}
    </>
  );
};

export default HistoryDialog;
